import { useCallback } from 'react';
import {
  GenerateUageReportOptions,
  GetByDayOptions,
  GetBySimsOptions,
  GetSimConnectivityOptions,
  connectivityClient,
  UpdateSimStatusOptions,
} from '@bymason/controller-api';
import { useGetDataHook, useSaveDataHook } from '@bymason/api-hooks-base';

export {
  SimModel,
  SimState,
  SimStates,
  SimType,
  UsageModel,
  UpdateSimStatusOptions,
  UpdateSimStatusSubet,
} from '@bymason/controller-api';

/**
 * React hook that asynchronously generates a custom usage report.
 * @returns HookArray
 * @HookArray [0] generateUsageReport method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGenerateUsageReport(clientToken: string) {
  const handleGenerateReport = useCallback(
    (options: GenerateUageReportOptions) => {
      return connectivityClient.generateUsageReport(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGenerateReport);
}

/**
 * React hook that asynchronously gets connectivity data by day.
 * @returns HookArray
 * @HookArray [0] getConnectivityByDay method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetUsageByDay(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetByDayOptions) => {
      return connectivityClient.getByDay(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets connectivity data by SIM.
 * @returns HookArray
 * @HookArray [0] getConnectivityBySim method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetUsageBySim(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetBySimsOptions) => {
      return connectivityClient.getBySim(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets connectivity data for a SIM.
 * @returns HookArray
 * @HookArray [0] getSimConnectivity method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetSimConnectivity(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetSimConnectivityOptions) => {
      return connectivityClient.getSimConnectivity(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously enables or disables a SIM.
 * @returns HookArray
 * @HookArray [0] updateSimStatus method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useUpdateSimStatus(clientToken: string) {
  const handleSaveData = useCallback(
    (options: UpdateSimStatusOptions) => {
      return connectivityClient.updateSimStatus(options, clientToken);
    },
    [clientToken]
  );

  return useSaveDataHook(handleSaveData);
}
